import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EventPositionsCard,
  EventPositionsCardSkeleton,
  HistoryCardSkeleton,
  HistoryEventCard,
  HistoryOrderCard,
  PortfolioChart,
  PortfolioOpenOrderCard,
  PortfolioOpenOrderCardSkeleton,
  PortfolioTabs,
  PortfolioValueHeader,
  PortfolioValueHeaderSkeleton,
  type PortfolioTab,
  type PortfolioTimeframe,
} from '@/components/features';
import { SellPopup, type SellPositionContext } from '@/components/features/BinaryMarkets/SellPopup';
import { LimitOrderPopup } from '@/components/features/BinaryMarkets/LimitOrderPopup';
import type { BuyMarketContext } from '@/components/features/BinaryMarkets/BuyInPopup';
import {
  useTradingOrderGetCounts,
  useTradingOrderListOrders,
  useTradingPositionGetPnl,
  useTradingPositionGetPortfolio,
  useTradingPositionListPositions,
} from '@/lib/endpoints';
import {
  PlaeePositionResponseDtoStatus,
  TradingOrderListOrdersStatus,
  TradingPositionGetPnlPeriod,
  TradingPositionListPositionsStatus,
} from '@/lib/models';
import type { PlaeeOrderResponseDto } from '@/lib/models';
import {
  byClosesAtAsc,
  groupFinishedPositionsByEvent,
  groupPositionsByEvent,
  type FinishedEventGroup,
} from '@/lib/trading/portfolio-groups';
import { useInfiniteCancelledOrders, useInfiniteFinishedPositions } from '@/lib/trading/useInfiniteHistory';
import { signedUsd } from '@/lib/trading/format';
import { useTradingRealtimeSync } from '@/lib/trading/useTradingRealtimeSync';
import { cn } from '@/lib/utils';

/** Chart pill → PnL query period. The series is daily, so there is no 1D pill. */
const PNL_PERIOD: Record<PortfolioTimeframe, TradingPositionGetPnlPeriod> = {
  '1W': TradingPositionGetPnlPeriod['1W'],
  '1M': TradingPositionGetPnlPeriod['1M'],
  All: TradingPositionGetPnlPeriod.ALL,
};

/** One rendered History card — a closed-event aggregate or a cancelled order. */
type HistoryCardItem =
  | { key: string; sortKey: string; group: FinishedEventGroup }
  | { key: string; sortKey: string; order: PlaeeOrderResponseDto };

/**
 * `/portfolio` route.
 *
 * The authenticated user's portfolio summary (`/plaee/positions/portfolio`),
 * a realized-P&L chart (`/plaee/positions/pnl`), and a tabbed list:
 * - **Positions** — open positions grouped per event, expandable rows with Sell
 *   (the sell sheet reuses the market page's {@link SellPopup}).
 * - **Open Orders** — resting orders with fill progress, Modify (reuses
 *   {@link LimitOrderPopup} in amend mode) and Cancel.
 * - **History** — one aggregate card per closed event (finished positions
 *   grouped, dollar Buy in / Exit / Quantity) plus one card per cancelled
 *   order, interleaved most-recently-finished first; fetched lazily when the
 *   tab activates.
 *
 * The Positions tab badge counts EVENT CARDS (derived from the loaded list);
 * Open Orders uses the counts endpoint. The top bar is owned by the app
 * shell's singleton `HomeTopBar`.
 */
export function PortfolioPage() {
  const [tab, setTab] = useState<PortfolioTab>('positions');
  const [timeframe, setTimeframe] = useState<PortfolioTimeframe>('All');
  const [sell, setSell] = useState<SellPositionContext | null>(null);
  const [modify, setModify] = useState<{ order: PlaeeOrderResponseDto; context: BuyMarketContext } | null>(null);

  // Keep positions / open orders / history / counts live off fills, sells and cancels.
  useTradingRealtimeSync();

  const { data: portfolio, isLoading: portfolioLoading } = useTradingPositionGetPortfolio();
  const { data: pnlPoints = [], isLoading: pnlLoading } = useTradingPositionGetPnl({ period: PNL_PERIOD[timeframe] });
  const { data: orderCounts } = useTradingOrderGetCounts();
  const { data: positionsRaw = [], isLoading: positionsLoading } = useTradingPositionListPositions({
    status: TradingPositionListPositionsStatus.open,
  });
  const { data: openOrders = [], isLoading: ordersLoading } = useTradingOrderListOrders({
    status: TradingOrderListOrdersStatus.open,
  });

  // History is fetched lazily when its tab activates. Both feeds page in
  // parallel; loaded items merge into one most-recently-finished-first list,
  // so cards can reorder/aggregate further while later pages stream in.
  const historyActive = tab === 'history';
  const positionsFeed = useInfiniteFinishedPositions(historyActive);
  const ordersFeed = useInfiniteCancelledOrders(historyActive);
  const finishedPositions = positionsFeed.data?.pages.flat() ?? [];
  const cancelledOrders = ordersFeed.data?.pages.flat() ?? [];
  const historyLoaded =
    positionsFeed.isSuccess && !positionsFeed.hasNextPage && ordersFeed.isSuccess && !ordersFeed.hasNextPage;
  const historyFetching = positionsFeed.isFetching || ordersFeed.isFetching;
  const loadMoreHistory = useCallback(() => {
    if (positionsFeed.hasNextPage && !positionsFeed.isFetching) void positionsFeed.fetchNextPage();
    if (ordersFeed.hasNextPage && !ordersFeed.isFetching) void ordersFeed.fetchNextPage();
  }, [positionsFeed, ordersFeed]);

  // One card per closed EVENT (aggregated positions) + one per cancelled order,
  // interleaved by finish/cancel time desc.
  const historyCards: HistoryCardItem[] = [
    ...groupFinishedPositionsByEvent(finishedPositions).map((group) => ({
      key: `event:${group.key}`,
      sortKey: group.finishedAt ?? '',
      group,
    })),
    ...cancelledOrders.map((order) => ({
      key: `order:${order.clientOrderId ?? order.orderId}`,
      sortKey: order.transactTime ?? '',
      order,
    })),
  ].sort((a, b) => (a.sortKey > b.sortKey ? -1 : a.sortKey < b.sortKey ? 1 : 0));

  const openPositions = positionsRaw.filter((p) => p.status === PlaeePositionResponseDtoStatus.OPEN && p.quantity > 0);
  const groups = groupPositionsByEvent(openPositions);
  // Soonest resolution first, matching the Positions groups.
  const restingOrders = openOrders.filter((o) => o.leavesQty > 0).sort(byClosesAtAsc);

  const value = portfolio?.totalValue ?? 0;
  // Empty portfolio (no open positions): value shows a muted $0 and the
  // "From Entry" delta line is hidden entirely (Figma 1777-50040).
  const hasOpenValue = Boolean(portfolio && (portfolio.totalValue > 0 || portfolio.totalCost > 0));
  const delta = portfolio?.totalUnrealizedPnl ?? 0;
  const deltaPercentage =
    portfolio && portfolio.totalCost > 0 ? (portfolio.totalUnrealizedPnl / portfolio.totalCost) * 100 : 0;
  const revenue = portfolio?.totalRealizedPnl ?? 0;
  // Header count matches the rendered cards: closed events + cancelled orders.
  const closedCount = historyCards.length;

  const handleModify = useCallback(
    (order: PlaeeOrderResponseDto, context: BuyMarketContext) => setModify({ order, context }),
    [],
  );

  return (
    <div className="flex flex-1 flex-col pb-[calc(82px+env(safe-area-inset-bottom))] md:pb-0">
      <div className="flex flex-col gap-4 px-4 md:px-6 lg:px-8 xl:px-10 w-full max-w-[600px] lg:max-w-[1300px] mx-auto">
        {/* Desktop (Figma 6499-163095): header + chart share one wide card; the
            chart drops its own card chrome inside it. Mobile keeps the stack. */}
        <div className="flex flex-col gap-4 lg:gap-2 lg:rounded-card lg:border lg:border-border lg:bg-surface lg:p-6 lg:shadow-card">
          {portfolioLoading ? (
            <PortfolioValueHeaderSkeleton />
          ) : (
            <PortfolioValueHeader
              value={value}
              muted={!hasOpenValue}
              delta={hasOpenValue ? delta : undefined}
              deltaPercentage={hasOpenValue ? deltaPercentage : undefined}
            />
          )}

          <PortfolioChart
            points={pnlPoints}
            timeframe={timeframe}
            onTimeframeChange={setTimeframe}
            isLoading={pnlLoading}
            className="lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none"
          />
        </div>

        <PortfolioTabs
          activeTab={tab}
          onTabChange={setTab}
          counts={{
            // Positions counts EVENT CARDS (not per-outcome positions), so it can
            // only come from the loaded list. While loading, show no number at
            // all rather than a misleading "(0)".
            positions: positionsLoading ? undefined : groups.length,
            openOrders: orderCounts?.open ?? (ordersLoading ? undefined : restingOrders.length),
          }}
        />

        {tab === 'positions' && positionsLoading && (
          <div className="flex flex-col gap-3 pb-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4">
            <EventPositionsCardSkeleton />
            <EventPositionsCardSkeleton />
          </div>
        )}
        {tab === 'positions' &&
          !positionsLoading &&
          (groups.length > 0 ? (
            <div className="flex flex-col gap-3 pb-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4">
              {groups.map((group) => (
                <EventPositionsCard key={group.key} group={group} onSell={setSell} />
              ))}
            </div>
          ) : (
            <EmptyState message="No Positions Yet." />
          ))}

        {tab === 'open-orders' && ordersLoading && (
          <div className="flex flex-col gap-3 pb-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4">
            <PortfolioOpenOrderCardSkeleton />
            <PortfolioOpenOrderCardSkeleton />
          </div>
        )}
        {tab === 'open-orders' &&
          !ordersLoading &&
          (restingOrders.length > 0 ? (
            <div className="flex flex-col gap-3 pb-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4">
              {restingOrders.map((order) => (
                <PortfolioOpenOrderCard key={order.clientOrderId ?? order.orderId} order={order} onModify={handleModify} />
              ))}
            </div>
          ) : (
            <EmptyState message="No open orders yet." />
          ))}

        {tab === 'history' &&
          (historyCards.length > 0 ? (
            <div className="flex flex-col gap-3 pb-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4">
              <div className="flex items-center gap-2 lg:col-span-2">
                <h2 className="text-base font-bold text-primary">
                  {closedCount} Closed Tab{closedCount === 1 ? '' : 's'}
                </h2>
                <span
                  className={cn(
                    'rounded-pill px-2.5 py-0.5 text-xs font-semibold',
                    revenue >= 0 ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger-alt',
                  )}
                >
                  Revenue {signedUsd(revenue)}
                </span>
              </div>
              {historyCards.map((item) =>
                'group' in item ? (
                  <HistoryEventCard key={item.key} group={item.group} />
                ) : (
                  <HistoryOrderCard key={item.key} order={item.order} />
                ),
              )}
              {!historyLoaded && (
                <div className="lg:col-span-2">
                  <LoadMoreSentinel onVisible={loadMoreHistory} />
                </div>
              )}
              {historyFetching && (
                <>
                  <HistoryCardSkeleton />
                  <HistoryCardSkeleton />
                </>
              )}
            </div>
          ) : historyFetching ? (
            <div className="flex flex-col gap-3 pb-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4">
              <HistoryCardSkeleton />
              <HistoryCardSkeleton />
              <HistoryCardSkeleton />
            </div>
          ) : (
            <EmptyState message="No history yet." />
          ))}
      </div>

      <SellPopup sell={sell} onClose={() => setSell(null)} />
      <LimitOrderPopup market={modify?.context ?? null} amendOrder={modify?.order ?? null} onClose={() => setModify(null)} />
    </div>
  );
}

/**
 * Infinite-scroll trigger — calls `onVisible` whenever it enters the viewport
 * (with a 300px head start so the next page lands before the user reaches the
 * end). The callback rides a ref so the observer is created once.
 */
function LoadMoreSentinel({ onVisible }: { onVisible: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  // Recreated when the callback changes — an IntersectionObserver is cheap,
  // and this keeps refs out of the render path (react-hooks/refs).
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onVisible();
      },
      { rootMargin: '300px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [onVisible]);
  return <div ref={ref} aria-hidden className="h-px" />;
}

/** Centered single-line empty-tab message (Figma 1777-50040 / 1782-53143 / 1782-53407). */
function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center py-12 text-center">
      <p className="text-base text-primary">{message}</p>
    </div>
  );
}
